import React, { useMemo, useState } from 'react';
import { saveCompanySmtpSettings, getCompanySmtpSettings } from '../services/smtpService';
import { getUserInfo } from '../utils/tokenManager';

const providerPresets = [
  { key: 'naver', name: '네이버', smtp: 'smtp.naver.com', port: 465 },
  { key: 'gmail', name: '구글(Gmail)', smtp: 'smtp.gmail.com', port: 465 },
  { key: 'daum', name: '다음(카카오)', smtp: 'smtp.daum.net', port: 465 },
  { key: 'nate', name: '네이트', smtp: 'smtp.mail.nate.com', port: 465 },
  { key: 'office365', name: '오피스365', smtp: 'smtp.office365.com', port: 587 },
  { key: 'outlook', name: '아웃룩(Outlook.com)', smtp: 'smtp-mail.outlook.com', port: 587 },
  { key: 'yahoo', name: '야후(Yahoo)', smtp: 'smtp.mail.yahoo.com', port: 465 },
  { key: 'custom', name: '기타(수기입력)', smtp: '', port: '' },
];

function toBase64Utf8(str) {
  try {
    return btoa(unescape(encodeURIComponent(String(str ?? ''))));
  } catch (_e) {
    // Fallback for already-ascii
    return btoa(String(str ?? ''));
  }
}

function fromBase64Utf8(b64) {
  try {
    return decodeURIComponent(escape(atob(String(b64 ?? ''))));
  } catch (_e) {
    try {
      return atob(String(b64 ?? ''));
    } catch (_e2) {
      return '';
    }
  }
}

export default function OutgoingMailManagement() {
  const { companyNo } = getUserInfo() || {};
  const [provider, setProvider] = useState('naver');
  const preset = useMemo(() => providerPresets.find(p => p.key === provider) || providerPresets[0], [provider]);

  const [smtpHost, setSmtpHost] = useState(preset.smtp);
  const [smtpPort, setSmtpPort] = useState(preset.port);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordEdited, setPasswordEdited] = useState(false);
  const [originalPasswordBase64, setOriginalPasswordBase64] = useState('');
  const [secure, setSecure] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loadedInfo, setLoadedInfo] = useState(null);
  const [loadedProvider, setLoadedProvider] = useState(null);

  // Sync preset when provider changes (non-custom)
  // - Do not override initially loaded settings for the same provider
  // - If user selects a different provider than loaded one, apply that provider's preset
  React.useEffect(() => {
    if (provider !== 'custom') {
      if (!loadedInfo) {
        setSmtpHost(preset.smtp);
        setSmtpPort(preset.port);
      } else if (loadedProvider && provider !== loadedProvider) {
        setSmtpHost(preset.smtp);
        setSmtpPort(preset.port);
      }
    }
  }, [provider, preset, loadedInfo, loadedProvider]);

  // Load existing settings on mount
  React.useEffect(() => {
    (async () => {
      try {
        const info = await getCompanySmtpSettings();
        if (info) {
          setLoadedInfo(info);
          const prov = String(info['서버업체'] || '').toLowerCase();
          const known = providerPresets.find(p => p.key === prov);
          setProvider(known ? prov : 'custom');
          setLoadedProvider(known ? prov : 'custom');
          setSmtpHost(String(info['서버주소'] || ''));
          setSmtpPort(info['포트'] ?? '');
          setSecure(Boolean(info['보안연결']));
          const decodedId = fromBase64Utf8(info['메일주소'] || '');
          setUsername(decodedId);
          setOriginalPasswordBase64(String(info['비밀번호'] || ''));
          setPassword('********');
          setPasswordEdited(false);
        }
      } catch (e) {
        // ignore load errors, show empty form
      }
    })();
  }, []);

  const canEditHostPort = provider === 'custom';
  const canSave = !!(companyNo && smtpHost && smtpPort && username && (passwordEdited ? password : originalPasswordBase64));

  const onSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!canSave) {
      setError('필수 값을 모두 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      await saveCompanySmtpSettings({
        smtpHost: smtpHost,
        smtpPort: String(smtpPort),
        smtpIdBase64: toBase64Utf8(username),
        smtpPasswordBase64: passwordEdited ? toBase64Utf8(password) : originalPasswordBase64,
        secure: !!secure,
        providerKey: provider,
      });
      setMessage('SMTP 설정이 저장되었습니다.');
    } catch (e2) {
      console.error(e2);
      setError('저장 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <div className="max-w-2xl mx-auto rounded-2xl bg-white p-5">
        <div>
          <h1 className="text-[17px] font-bold text-gray-900">발신메일 관리</h1>
          <p className="text-sm text-gray-500 mt-1">메일 업체 선택 시 SSL/TLS 권장값으로 SMTP 주소와 포트가 자동 설정됩니다. '기타(수기입력)'을 선택하면 직접 입력할 수 있습니다.</p>
        </div>
        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">메일업체</label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="h-9 w-full rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
            >
              {providerPresets.map(p => (
                <option key={p.key} value={p.key}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="text-[13px] font-semibold text-gray-600">SMTP 서버</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">SMTP 주소</label>
              <input
                type="text"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                disabled={!canEditHostPort}
                placeholder="smtp.example.com"
                className={`h-9 w-full rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none ${!canEditHostPort ? 'bg-gray-50' : ''}`}
              />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">포트</label>
              <input
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                disabled={!canEditHostPort}
                placeholder="465 또는 587"
                className={`h-9 w-full rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none ${!canEditHostPort ? 'bg-gray-50' : ''}`}
              />
            </div>
          </div>

          <div className="text-[13px] font-semibold text-gray-600">인증</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">아이디</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="example@domain.com"
                className="h-9 w-full rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">비밀번호</label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordEdited(true); }}
                className="h-9 w-full rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                autoComplete="current-password"
                placeholder={originalPasswordBase64 ? '********' : ''}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={secure}
                onChange={(e) => setSecure(e.target.checked)}
                className="rounded border-gray-300"
              />
              보안연결(SSL/TLS) 사용
            </label>
            <button
              type="submit"
              disabled={!canSave || saving}
              className={`h-9 rounded-lg px-3.5 text-sm font-semibold text-white ${(!canSave || saving) ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>

          {message && <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg p-2">{message}</div>}
          {error && <div className="text-xs text-red-700 bg-red-50 rounded-lg p-2">{error}</div>}
        </form>
      </div>
      <div className="max-w-2xl mx-auto mt-2 text-[11px] text-gray-500">
        참고: 각 메일 업체는 발신 권한/2단계 인증/앱 비밀번호 설정이 필요할 수 있습니다.
      </div>
    </div>
  );
}
